import * as z from "zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { settingsRepo } from "@/lib/repositories";
import { useSettingsStore } from "../stores";
import useAuth from "@/features/auth/hooks/useAuth";
import { useAuthStore } from "@/stores/useAuthStore";

const accountInfoSchema = z.object({
  username: z.string().min(1),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
});

const AccountInfoSetting = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const saveTrigger = useSettingsStore((s) => s.saveTrigger);
  const setIsSaving = useSettingsStore((s) => s.setIsSaving);

  const form = useForm<z.infer<typeof accountInfoSchema>>({
    resolver: zodResolver(accountInfoSchema),
    defaultValues: {
      username: user?.username ?? "",
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
    },
  });

  const onSubmit = async (data: z.infer<typeof accountInfoSchema>) => {
    if (!user?.id) return;
    setIsSaving(true);
    const [error] = await catchError(
      settingsRepo.updateAccountInfo(user.id, data),
    );
    if (error) {
      toast.error(handleApiError(error).message);
    } else {
      setUser(JSON.stringify({ ...user, ...data }));
      toast.success(t("settings.account_info.save_success"));
    }
    setIsSaving(false);
  };

  useEffect(() => {
    if (saveTrigger === 0) return;
    form.handleSubmit(onSubmit)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTrigger]);

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2 text-sm font-medium">
        <User className="size-4" />
        {t("settings.account_info.title")}
      </Label>
      <FieldGroup className="gap-y-4">
        <Controller
          name="username"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="username-setting-input">
                {t("keys.username")}
              </FieldLabel>
              <Input
                {...field}
                id="username-setting-input"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
        <Controller
          name="first_name"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="first-name-setting-input">
                {t("keys.first_name")}
              </FieldLabel>
              <Input
                {...field}
                id="first-name-setting-input"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
        <Controller
          name="last_name"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="last-name-setting-input">
                {t("keys.last_name")}
              </FieldLabel>
              <Input
                {...field}
                id="last-name-setting-input"
                aria-invalid={fieldState.invalid}
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>
    </div>
  );
};

export default AccountInfoSetting;
